import { platformBadge } from './platform-colors';
import { scopeOptionLabel, useScopes } from './use-scopes';

/**
 * Bolinha com a inicial do scope e tooltip com o nome — o mesmo padrão das
 * plataformas na lista de usuários.
 *
 * O id não aparece: nem na bolinha, nem no tooltip. Ele só reaparece como
 * último recurso, quando não há nome nenhum a exibir (lista de scopes ainda
 * carregando, indisponível, ou id que não está nela).
 */
export function ScopeBadge({ id }: { id: string }) {
  const { data: scopes } = useScopes();
  const scope = scopes?.find((s) => s.id === id);

  // Enquanto a lista não chega — ou se o id não estiver nela — não dá para
  // saber a plataforma; a bolinha fica neutra e o tooltip mostra o id.
  const { color, initial } = platformBadge(scope?.platform);

  // `scopeOptionLabel` devolve o próprio id quando a resposta vem sem nome —
  // nesse caso não há nome de verdade a mostrar.
  const label = scope ? scopeOptionLabel(scope) : null;
  const name = label && label !== id ? label : null;

  return (
    <div className="group relative inline-flex">
      <span
        className={`flex h-7 w-7 cursor-default items-center justify-center rounded-full text-[10px] font-bold text-white ${color}`}
      >
        {initial}
      </span>
      <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2.5 py-1.5 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 dark:bg-neutral-700">
        {/* Só cai no id quando não há nome — aí ele é a única identificação. */}
        {name ? (
          <span className="font-medium">{name}</span>
        ) : (
          <span className="font-mono">{id}</span>
        )}
        <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-neutral-900 dark:border-t-neutral-700" />
      </div>
    </div>
  );
}

/** Vários scopes lado a lado; lista vazia vira travessão. */
export function ScopeBadgeList({ ids }: { ids: string[] }) {
  if (ids.length === 0) {
    return <span className="text-xs text-neutral-400">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {ids.map((id) => (
        <ScopeBadge key={id} id={id} />
      ))}
    </div>
  );
}
